import type { LookupCategory } from '@stfors/shared-types';
import { useLookups, activeOptionsFor, labelForValue } from '../api/queries/lookups';

// One reusable dropdown bound to a Settings → Lookups category. Replaces the
// hand-written empty / (legacy) / .map select triad that used to be duplicated
// across every form. Fetches its own category (per-category, 5-min cached), so
// a page only loads the option lists it actually renders.
//
// Display is `label ?? value` (code-backed categories show the human label;
// free-text categories show the value). The selected/submitted value is always
// the option's `value` (the stable code). A stored value no longer in the
// active set still renders as a "(legacy)" option so edits don't silently drop
// it.
interface LookupSelectProps {
  category: LookupCategory;
  value: string;
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  /** Render a leading blank option (for optional fields / "any" filters). */
  allowEmpty?: boolean;
  /** Text for the blank option (default "—"). */
  emptyLabel?: string;
  /** What to show as each option's text. 'label' (default) shows `label ?? value`;
   *  'value' shows the raw code — used where a compact code is wanted (e.g. the
   *  us_state dropdown shows "CA", not "California"). */
  optionText?: 'label' | 'value';
  'aria-label'?: string;
}

export function LookupSelect({
  category,
  value,
  onChange,
  id,
  name,
  className = 'form-input',
  disabled = false,
  required = false,
  allowEmpty = false,
  emptyLabel = '—',
  optionText = 'label',
  'aria-label': ariaLabel,
}: LookupSelectProps): JSX.Element {
  const { data } = useLookups(category);
  const options = activeOptionsFor(data?.lookup_options, category);
  const knownCurrent = value !== '' && options.some((o) => o.value === value);
  const legacyText = optionText === 'value'
    ? value
    : labelForValue(data?.lookup_options, category, value);

  // Empty selection shows the emptyLabel as a muted placeholder (see
  // tokens.css `select.form-input.is-placeholder`). Real options stay white.
  const isPlaceholder = allowEmpty && value === '';
  const selectClass = [className, isPlaceholder ? 'is-placeholder' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <select
      id={id}
      name={name}
      className={selectClass}
      value={value}
      disabled={disabled}
      required={required}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
    >
      {allowEmpty && <option value="">{emptyLabel}</option>}
      {value !== '' && !knownCurrent && (
        <option value={value}>{legacyText} (legacy)</option>
      )}
      {options.map((o) => (
        <option key={o.id} value={o.value}>
          {optionText === 'value' ? o.value : (o.label ?? o.value)}
        </option>
      ))}
    </select>
  );
}
